import { useEffect, useState, useCallback } from 'react';
import api from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Plus, Check, X, Trash } from '@phosphor-icons/react';
import { toast } from 'sonner';

// Phase 2 — Leave Management. Staff requests + admin approve/reject.

const LEAVE_TYPES = [
  { value: 'casual',    label: 'Casual Leave',    cls: 'bg-[#DBEAFE] text-[#1D4ED8] border-[#BFDBFE]' },
  { value: 'sick',      label: 'Sick Leave',      cls: 'bg-[#FEE2E2] text-[#B91C1C] border-[#FCA5A5]' },
  { value: 'emergency', label: 'Emergency',       cls: 'bg-[#FFEDD5] text-[#9A3412] border-[#FDBA74]' },
  { value: 'half_day',  label: 'Half Day',        cls: 'bg-[#FEF3C7] text-[#92400E] border-[#FDE68A]' },
  { value: 'unpaid',    label: 'Unpaid',          cls: 'bg-[#F4F4F5] text-[#52525B] border-[#E4E4E7]' },
];
const TYPE_BY_KEY = Object.fromEntries(LEAVE_TYPES.map(t => [t.value, t]));

const STATUS_STYLES = {
  pending:  { label: 'Pending',  cls: 'bg-[#FEF3C7] text-[#92400E] border-[#FDE68A]' },
  approved: { label: 'Approved', cls: 'bg-[#DCFCE7] text-[#15803D] border-[#86EFAC]' },
  rejected: { label: 'Rejected', cls: 'bg-[#FEE2E2] text-[#B91C1C] border-[#FCA5A5]' },
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return iso; }
};

// Inclusive day count between two YYYY-MM-DD strings.
const dayCount = (from, to, halfDay) => {
  if (!from) return 0;
  if (halfDay) return 0.5;
  const f = new Date(from); const t = new Date(to || from);
  return Math.max(1, Math.round((t - f) / 86400000) + 1);
};

function RequestDialog({ open, onOpenChange, onSaved }) {
  const [form, setForm] = useState({ leave_type: 'casual', from_date: '', to_date: '', half_day: false, reason: '' });

  useEffect(() => {
    if (open) setForm({ leave_type: 'casual', from_date: '', to_date: '', half_day: false, reason: '' });
  }, [open]);

  const submit = async () => {
    if (!form.from_date) { toast.error('From date required'); return; }
    if (!form.reason.trim()) { toast.error('Reason required'); return; }
    try {
      await api.post('/leaves', form);
      toast.success('Leave requested — admin will be notified');
      onSaved();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-xl border-[#E4E4E7] max-w-lg">
        <DialogHeader>
          <DialogTitle>Request Leave</DialogTitle>
          <DialogDescription>Pick the date(s) and type. Your request goes to admin for approval.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <Label className="text-[10px] uppercase tracking-[0.12em] font-semibold text-[#52525B]">Leave Type</Label>
            <Select value={form.leave_type} onValueChange={v => setForm({ ...form, leave_type: v, half_day: v === 'half_day' || form.half_day })}>
              <SelectTrigger className="rounded-lg mt-1" data-testid="leave-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                {LEAVE_TYPES.map(t => (<SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.12em] font-semibold text-[#52525B]">From Date</Label>
            <Input type="date" value={form.from_date} onChange={e => setForm({ ...form, from_date: e.target.value })} className="rounded-lg mt-1" data-testid="leave-from" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.12em] font-semibold text-[#52525B]">To Date</Label>
            <Input type="date" value={form.to_date} disabled={form.half_day || form.leave_type === 'half_day'} onChange={e => setForm({ ...form, to_date: e.target.value })} className="rounded-lg mt-1" data-testid="leave-to" />
          </div>
          <div className="col-span-2">
            <label className="flex items-center gap-2 text-sm text-[#52525B] cursor-pointer">
              <input type="checkbox" checked={form.half_day || form.leave_type === 'half_day'} onChange={e => setForm({ ...form, half_day: e.target.checked })} className="rounded h-4 w-4 accent-[#3B82F6]" data-testid="leave-half-day" />
              Half day only
            </label>
          </div>
          <div className="col-span-2">
            <Label className="text-[10px] uppercase tracking-[0.12em] font-semibold text-[#52525B]">Reason *</Label>
            <Textarea rows={3} value={form.reason} onChange={e => setForm({ ...form, reason: e.target.value })} className="rounded-lg mt-1" placeholder="Brief reason for the leave" data-testid="leave-reason" />
          </div>
        </div>
        <DialogFooter>
          <Button onClick={submit} className="bg-[#3B82F6] text-white hover:bg-[#2563EB] hover:text-white rounded-xl" data-testid="submit-leave-btn">
            Submit Request
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DecideDialog({ target, action, onClose, onDecided }) {
  const [remarks, setRemarks] = useState('');
  const isApprove = action === 'approved';
  useEffect(() => { setRemarks(''); }, [target]);

  const submit = async () => {
    try {
      await api.put(`/leaves/${target.id}/decide`, { status: action, admin_remarks: remarks });
      toast.success(`Leave ${isApprove ? 'approved' : 'rejected'}`);
      onDecided();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  return (
    <Dialog open={!!target} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="rounded-xl border-[#E4E4E7] max-w-md">
        <DialogHeader>
          <DialogTitle>{isApprove ? 'Approve' : 'Reject'} — {target?.user_name}</DialogTitle>
          <DialogDescription>{target?.leave_type} · {fmtDate(target?.from_date)} → {fmtDate(target?.to_date)}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="bg-[#FAFAFA] border border-[#E4E4E7] rounded-lg p-3">
            <p className="text-[10px] uppercase tracking-[0.12em] font-semibold text-[#52525B] mb-1">Staff Reason</p>
            <p className="text-sm text-[#0A0A0A] italic">"{target?.reason}"</p>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.12em] font-semibold text-[#52525B]">Admin Remarks (optional)</Label>
            <Textarea rows={2} value={remarks} onChange={e => setRemarks(e.target.value)} className="rounded-lg mt-1" data-testid="decide-remarks" />
          </div>
        </div>
        <DialogFooter>
          <Button
            onClick={submit}
            className={`rounded-xl text-white ${isApprove ? 'bg-[#10B981] hover:bg-[#059669] hover:text-white' : 'bg-[#EF4444] hover:bg-[#DC2626] hover:text-white'}`}
            data-testid="decide-confirm-btn"
          >
            {isApprove ? 'Confirm Approval' : 'Confirm Rejection'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function LeavePage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [leaves, setLeaves] = useState([]);
  const [tab, setTab] = useState('pending');
  const [showReq, setShowReq] = useState(false);
  const [decideTarget, setDecideTarget] = useState(null);
  const [decideAction, setDecideAction] = useState('approved');

  const refresh = useCallback(() => {
    api.get('/leaves').then(r => setLeaves(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const filtered = leaves.filter(l => l.status === tab);
  const counts = { pending: 0, approved: 0, rejected: 0 };
  leaves.forEach(l => { counts[l.status] = (counts[l.status] || 0) + 1; });

  const cancel = async (l) => {
    if (!window.confirm(`Withdraw this leave request?`)) return;
    try {
      await api.delete(`/leaves/${l.id}`);
      toast.success('Leave withdrawn');
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-5" data-testid="leave-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <p className="text-xs text-[#52525B]">
          {isAdmin ? 'Review and decide on staff leave requests.' : 'Request leave and track approval status.'}
        </p>
        <Button onClick={() => setShowReq(true)} className="bg-[#3B82F6] text-white hover:bg-[#2563EB] hover:text-white rounded-xl" data-testid="request-leave-btn">
          <Plus size={16} className="mr-1.5" /> Request Leave
        </Button>
      </div>

      <div className="flex gap-2 border-b border-[#E4E4E7]">
        {['pending', 'approved', 'rejected'].map(t => {
          const meta = STATUS_STYLES[t];
          return (
            <button
              key={t}
              type="button"
              onClick={() => setTab(t)}
              data-testid={`tab-${t}`}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                tab === t ? 'border-[#3B82F6] text-[#3B82F6]' : 'border-transparent text-[#52525B] hover:text-[#0A0A0A]'
              }`}
            >
              {meta.label} <span className="ml-1 text-xs font-mono">({counts[t] || 0})</span>
            </button>
          );
        })}
      </div>

      <Card className="rounded-xl border-[#E4E4E7] shadow-sm">
        <CardContent className="p-0">
          {filtered.length === 0 ? (
            <p className="text-sm text-[#52525B] text-center py-8">No {tab} leaves.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-[#E4E4E7]">
                  {isAdmin && <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold">Staff</TableHead>}
                  <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold">Type</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold">From</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold">To</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold text-right">Days</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold">Reason</TableHead>
                  {tab !== 'pending' && <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold">Admin Remarks</TableHead>}
                  <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map(l => {
                  const typeMeta = TYPE_BY_KEY[l.leave_type] || LEAVE_TYPES[0];
                  return (
                    <TableRow key={l.id} className="border-[#E4E4E7]" data-testid={`leave-row-${l.id}`}>
                      {isAdmin && <TableCell className="text-xs font-medium">{l.user_name}</TableCell>}
                      <TableCell>
                        <Badge className={`rounded-md text-[10px] ${typeMeta.cls}`} variant="outline">{typeMeta.label}</Badge>
                        {l.half_day && <Badge className="rounded-md text-[10px] ml-1 bg-[#FEF3C7] text-[#92400E] border-[#FDE68A]" variant="outline">½</Badge>}
                      </TableCell>
                      <TableCell className="text-xs font-mono">{fmtDate(l.from_date)}</TableCell>
                      <TableCell className="text-xs font-mono">{fmtDate(l.to_date)}</TableCell>
                      <TableCell className="text-xs text-right font-mono">{dayCount(l.from_date, l.to_date, l.half_day)}</TableCell>
                      <TableCell className="text-xs text-[#52525B] max-w-[200px] truncate" title={l.reason}>{l.reason}</TableCell>
                      {tab !== 'pending' && (
                        <TableCell className="text-xs text-[#52525B] max-w-[200px] truncate" title={l.admin_remarks || ''}>
                          {l.admin_remarks || '—'}
                          {l.approved_by_name && <p className="text-[10px] text-[#A1A1AA]">by {l.approved_by_name}</p>}
                        </TableCell>
                      )}
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          {l.status === 'pending' && isAdmin && (
                            <>
                              <Button size="sm" variant="outline" className="h-7 px-2 text-xs border-[#10B981] text-[#10B981] hover:bg-[#DCFCE7]" onClick={() => { setDecideTarget(l); setDecideAction('approved'); }} data-testid={`approve-${l.id}`}>
                                <Check size={12} className="mr-1" /> Approve
                              </Button>
                              <Button size="sm" variant="outline" className="h-7 px-2 text-xs border-[#EF4444] text-[#EF4444] hover:bg-[#FEE2E2]" onClick={() => { setDecideTarget(l); setDecideAction('rejected'); }} data-testid={`reject-${l.id}`}>
                                <X size={12} className="mr-1" /> Reject
                              </Button>
                            </>
                          )}
                          {l.status === 'pending' && (l.user_id === user.id || isAdmin) && (
                            <Button size="sm" variant="ghost" className="h-7 px-2 text-xs text-[#EF4444]" onClick={() => cancel(l)} data-testid={`withdraw-${l.id}`}>
                              <Trash size={14} />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <RequestDialog open={showReq} onOpenChange={setShowReq} onSaved={refresh} />
      <DecideDialog target={decideTarget} action={decideAction} onClose={() => setDecideTarget(null)} onDecided={refresh} />
    </div>
  );
}
